import {
  DT_MS,
  MAX_FRAME_MS,
  MAX_STEPS_PER_FRAME,
  UI_PUBLISH_INTERVAL_MS,
  type Command,
  type SimEvent,
  type TimedCommand,
  type World,
} from '@prism/sim';

/**
 * Injection seam for the frame clock.
 *
 * The loop's whole job is to make sure real time cannot influence the simulation's
 * outcome — which is a claim that has to be tested, not asserted. Passing in a
 * deterministic scheduler lets the frame flow be driven tick-by-tick under Node, so the
 * "4× hashes identically to 1×" and "hitstop changes nothing" guarantees are covered by
 * real tests rather than by a comment.
 */
export interface FrameScheduler {
  now(): number;
  request(callback: (timestamp: number) => void): number;
  cancel(handle: number): void;
}

const rafScheduler: FrameScheduler = {
  now: () => performance.now(),
  request: (callback) => requestAnimationFrame(callback),
  cancel: (handle) => cancelAnimationFrame(handle),
};

export interface LoopHooks {
  /** Interpolate rendered positions between the previous and current tick (TRD-03 R-03-12). */
  onSync?: (alpha: number) => void;
  /** Drain this tick's events for VFX and audio (TRD-04 R-04-01). */
  onEvents?: (events: readonly SimEvent[]) => void;
  /** Real elapsed ms — VFX is deliberately NOT tick-locked (TRD-04 R-04-02). */
  onVfx?: (realDeltaMs: number) => void;
  /** Throttled to ~20 Hz, not every tick (TRD-00 §6). */
  onPublish?: () => void;
}

/**
 * The frame driver — TRD-00 §6.
 *
 * This class is the only place where real time touches the simulation, and it is written
 * so that it *cannot* influence the outcome. Speed and hitstop change how many ticks are
 * requested and when; they never change what a tick does. That is why a run played at 4×
 * with hitstop enabled hashes identically to the same run played at 1× with it off
 * (TRD-00 §5.1, TRD-04 R-04-14).
 */
export class GameLoop {
  private accumulator = 0;
  private lastTimestamp = 0;
  private publishAccumulator = 0;
  private hitstopRemainingMs = 0;
  private hitstopBudgetMs = 0;
  private rafHandle: number | null = null;
  private pendingCommands: Command[] = [];

  /**
   * A plain number, not `SpeedMultiplier`: the union is what the PLAYER may choose, and
   * the idle skip drives this past it to `IDLE_SKIP_SPEED` on its own.
   */
  speed: number = 1;

  /** Rolling FPS estimate, for the debug overlay only. Never read by the sim. */
  fps = 0;

  /**
   * The complete input history of this run. This IS the save file (TRD-10 R-10-06) and
   * the payload the server re-simulates to validate a score (TRD-11 §5).
   */
  readonly commandLog: TimedCommand[] = [];

  constructor(
    private readonly world: World,
    private readonly hooks: LoopHooks = {},
    private readonly scheduler: FrameScheduler = rafScheduler,
  ) {}

  start(): void {
    if (this.rafHandle !== null) return;
    this.lastTimestamp = this.scheduler.now();
    this.accumulator = 0;
    this.rafHandle = this.scheduler.request(this.frame);
  }

  stop(): void {
    if (this.rafHandle === null) return;
    this.scheduler.cancel(this.rafHandle);
    this.rafHandle = null;
  }

  get running(): boolean {
    return this.rafHandle !== null;
  }

  /**
   * Queue a player action for the next tick.
   *
   * Every command is logged, including ones the sim will reject as no-ops — filtering
   * client-side would give the server a different stream than the client ran
   * (TRD-07 R-07-08).
   */
  dispatch(cmd: Command): void {
    this.pendingCommands.push(cmd);
  }

  /**
   * Freeze the sim for `ms` of real time while VFX keeps running (TRD-04 §5).
   *
   * Capped at 250 ms per real second and disabled entirely at 4×, or a dense wave
   * stutters permanently (TRD-04 R-04-13).
   */
  freeze(ms: number): void {
    if (this.speed >= 4) return;
    const allowed = Math.min(ms, this.hitstopBudgetMs);
    if (allowed <= 0) return;
    this.hitstopRemainingMs += allowed;
    this.hitstopBudgetMs -= allowed;
  }

  private readonly frame = (timestamp: number): void => {
    this.rafHandle = this.scheduler.request(this.frame);

    // Clamped so a backgrounded tab returning after 30 s does not try to catch up.
    const rawElapsed = timestamp - this.lastTimestamp;
    const elapsed = Math.min(rawElapsed, MAX_FRAME_MS);
    this.lastTimestamp = timestamp;

    this.fps = this.fps * 0.9 + (rawElapsed > 0 ? 1000 / rawElapsed : 0) * 0.1;
    this.hitstopBudgetMs = Math.min(250, this.hitstopBudgetMs + elapsed * 0.25);

    if (this.hitstopRemainingMs > 0) {
      // Frozen wall-time is DISCARDED rather than banked. Ticks are delayed, never
      // dropped and never doubled up afterwards — that is what keeps the hash identical.
      this.hitstopRemainingMs -= elapsed;
    } else {
      this.accumulator += elapsed * this.speed;
    }

    const maxSteps = MAX_STEPS_PER_FRAME * this.speed;
    let steps = 0;
    while (this.accumulator >= DT_MS && steps < maxSteps) {
      const commands = this.takePendingCommands();
      this.world.step(commands);

      if (this.world.events.length > 0) {
        this.hooks.onEvents?.(this.world.events);
      }

      this.accumulator -= DT_MS;
      steps++;
    }

    // Bleed off any backlog we could not consume, so we do not spiral.
    if (steps === maxSteps) {
      this.accumulator = 0;
    }

    this.hooks.onSync?.(this.accumulator / DT_MS);
    this.hooks.onVfx?.(elapsed);

    this.publishAccumulator += elapsed;
    if (this.publishAccumulator >= UI_PUBLISH_INTERVAL_MS) {
      this.publishAccumulator = 0;
      this.hooks.onPublish?.();
    }
  };

  private takePendingCommands(): readonly Command[] {
    if (this.pendingCommands.length === 0) return EMPTY;

    const commands = this.pendingCommands;
    this.pendingCommands = [];

    for (const cmd of commands) {
      this.commandLog.push({ tick: this.world.tick, cmd });
    }
    return commands;
  }
}

const EMPTY: readonly Command[] = [];
